import React, { useState, useCallback } from "react";
import { useGame } from "../context/GameContext";

const AVATARS = [
  { emoji: "🦊", bg: "#ff3e3e22", border: "#ff3e3e", text: "#ff3e3e" },
  { emoji: "👾", bg: "#9b6dff22", border: "#9b6dff", text: "#9b6dff" },
  { emoji: "🦖", bg: "#3effa322", border: "#3effa3", text: "#3effa3" },
  { emoji: "🐱", bg: "#f5c84222", border: "#f5c842", text: "#f5c842" },
  { emoji: "🦁", bg: "#ff6b3e22", border: "#ff6b3e", text: "#ff6b3e" },
  { emoji: "🐧", bg: "#3eaaff22", border: "#3eaaff", text: "#3eaaff" },
  { emoji: "🐷", bg: "#ff3eb522", border: "#ff3eb5", text: "#ff3eb5" },
  { emoji: "🐬", bg: "#3effee22", border: "#3effee", text: "#3effee" },
  { emoji: "👽", bg: "#aaff3e22", border: "#aaff3e", text: "#aaff3e" },
  { emoji: "👻", bg: "#ffffff22", border: "#ffffff", text: "#ffffff" },
];

// URL do servidor — configurada no build ou usa o mesmo domínio/path da página
const API_BASE = process.env.REACT_APP_API_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : `${window.location.protocol}//${window.location.host}/impostor-api`);

// ── Player YouTube embutido ────────────────────────────────────
function YouTubePlayer({ videoId }) {
  if (!videoId) return null;
  return (
    <div style={{
      position: "relative", paddingBottom: "56.25%", height: 0,
      borderRadius: 12, overflow: "hidden", border: "2px solid #3eaaff40",
    }}>
      <iframe
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
        title="Player"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
      />
    </div>
  );
}

// ── Busca de música ────────────────────────────────────────────
function BuscaMusica({ onSelecionada }) {
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [buscou, setBuscou] = useState(false);

  const buscar = useCallback(async () => {
    if (!query.trim()) return;
    setCarregando(true);
    setErro("");
    setBuscou(true);
    try {
      const res = await fetch(`${API_BASE}/api/buscar-musica?q=${encodeURIComponent(query.trim())}`);
      const data = await res.json();
      if (data.erro) {
        setErro(data.erro);
        setResultados([]);
      } else {
        setResultados(data.resultados || []);
        if ((data.resultados || []).length === 0) setErro("Nenhuma música encontrada. Tente outro nome.");
      }
    } catch (e) {
      setErro("Erro ao conectar com o servidor. Verifique a conexão.");
      setResultados([]);
    }
    setCarregando(false);
  }, [query]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Campo de busca */}
      <div className="input-group" style={{ marginBottom: 0 }}>
        <label className="input-label">🔍 Buscar música</label>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            className="input-field"
            style={{ marginBottom: 0, flex: 1 }}
            placeholder="Ex: Bohemian Rhapsody, Beyoncé, funk..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") buscar(); }}
            autoFocus
          />
          <button
            className="btn btn-primary"
            onClick={buscar}
            disabled={carregando || !query.trim()}
            style={{ padding: "0 20px", whiteSpace: "nowrap" }}
          >
            {carregando ? "..." : "🔍"}
          </button>
        </div>
      </div>

      {/* Erro */}
      {erro && (
        <div style={{ background: "var(--accent-dim)", border: "1px solid var(--border-accent)", borderRadius: 10, padding: "12px 14px", fontSize: "0.84rem", color: "var(--accent)" }}>
          ⚠️ {erro}
        </div>
      )}

      {/* Loading */}
      {carregando && (
        <div style={{ textAlign: "center", padding: "20px", color: "var(--text-muted)", fontSize: "0.88rem" }}>
          🔍 Buscando músicas...
        </div>
      )}

      {/* Resultados */}
      {!carregando && resultados.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            {resultados.length} resultado{resultados.length !== 1 ? "s" : ""}
          </div>
          {resultados.map(r => (
            <button
              key={r.videoId}
              onClick={() => onSelecionada(r)}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                background: "var(--bg-elevated)", border: "1px solid var(--border)",
                borderRadius: 12, padding: "10px 12px", cursor: "pointer",
                textAlign: "left", transition: "border-color 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#3eaaff"; e.currentTarget.style.boxShadow = "0 0 16px #3eaaff30"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.boxShadow = "none"; }}
            >
              {r.thumb && (
                <img src={r.thumb} alt="" style={{ width: 64, height: 48, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.titulo}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: 2 }}>
                  {r.canal}
                </div>
              </div>
              <div style={{ color: "#3eaaff", fontSize: "1.2rem", flexShrink: 0 }}>▶</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tela principal do DJ ───────────────────────────────────────
export function MusicaDJScreen() {
  const {
    jogadores, musicaDJIdx, musicaRodada, musicaAcertou,
    ninguemAcertouMusica, musicaPlacar, numRodadas, finalizarMusica,
  } = useGame();

  const [vistoDJ, setVistoDJ] = useState(false);
  const [musicaSelecionada, setMusicaSelecionada] = useState(null); // { videoId, titulo, canal }
  const [fase, setFase] = useState("busca"); // busca | tocando
  const [vencedor, setVencedor] = useState(null);
  const [ninguem, setNinguem] = useState(false);

  const avDJ = AVATARS[musicaDJIdx % AVATARS.length];
  const nomeDJ = jogadores[musicaDJIdx];
  const ouvintes = jogadores.map((n, i) => ({ nome: n, idx: i })).filter(j => j.idx !== musicaDJIdx);

  const confirmarVencedor = () => {
    if (ninguem) { ninguemAcertouMusica(); return; }
    if (vencedor !== null) musicaAcertou(vencedor);
  };

  const handleSelecionarMusica = (resultado) => {
    setMusicaSelecionada(resultado);
    setFase("tocando");
  };

  // ── Tela de passagem (privada do DJ) ──────────────────
  if (!vistoDJ) return (
    <div className="app-wrapper" style={{ paddingTop: 80 }}>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{
          width: 80, height: 80, borderRadius: "50%", background: avDJ.bg,
          border: `3px solid ${avDJ.border}`, color: avDJ.text,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "2.8rem", margin: "0 auto 12px", boxShadow: `0 0 30px ${avDJ.border}44`,
        }}>{avDJ.emoji}</div>
        <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.8rem" }}>{nomeDJ}</div>
        <div style={{ color: "var(--text-muted)", fontSize: "0.8rem", marginTop: 4 }}>é o DJ desta rodada</div>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 10 }}>
          <span style={{ background: "rgba(62,170,255,0.15)", border: "1px solid #3eaaff", color: "#3eaaff", borderRadius: 20, padding: "4px 14px", fontSize: "0.78rem", fontFamily: "'Space Mono'" }}>
            Rodada {musicaRodada}/{numRodadas}
          </span>
        </div>
      </div>
      <div className="card" style={{ maxWidth: 420 }}>
        <div className="card-body" style={{ textAlign: "center" }}>
          <div style={{ fontSize: "3rem", marginBottom: 12 }}>🎵</div>
          <p style={{ color: "var(--text-secondary)", fontSize: "0.95rem", lineHeight: 1.6, marginBottom: 24 }}>
            Apenas <strong style={{ color: avDJ.text }}>{nomeDJ}</strong> deve ver esta tela.<br /><br />
            Você vai <strong style={{ color: "#3eaaff" }}>buscar uma música pelo nome</strong> e tocar para os outros adivinharem.
          </p>
          <button className="btn btn-primary btn-full" onClick={() => setVistoDJ(true)}>
            🎵 Sou o DJ, começar
          </button>
        </div>
      </div>
    </div>
  );

  // ── Fase: buscar música ────────────────────────────────
  if (fase === "busca") return (
    <div className="app-wrapper" style={{ paddingTop: 80, paddingBottom: 40 }}>
      <div className="card" style={{ maxWidth: 520 }}>
        <div className="card-header">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div className="title-eyebrow">🎵 Adivinhe a Música · Rodada {musicaRodada}/{numRodadas}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: avDJ.bg, border: `2px solid ${avDJ.border}`, color: avDJ.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem" }}>{avDJ.emoji}</div>
                <div>
                  <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.2rem" }}>{nomeDJ}</div>
                  <div style={{ color: "var(--text-muted)", fontSize: "0.72rem" }}>Escolha a música em segredo</div>
                </div>
              </div>
            </div>
            {/* Mini placar */}
            <div style={{ textAlign: "right" }}>
              {Object.entries(musicaPlacar)
                .filter(([, pts]) => pts > 0)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([idx, pts]) => {
                  const av = AVATARS[Number(idx) % AVATARS.length];
                  return (
                    <div key={idx} style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end", marginBottom: 2 }}>
                      <span style={{ fontSize: "0.9rem" }}>{av.emoji}</span>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>{jogadores[Number(idx)]?.split(" ")[0]}</span>
                      <span style={{ fontFamily: "'Bebas Neue'", color: "#3eaaff", fontSize: "1rem" }}>{pts}</span>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
        <div className="card-body">
          <BuscaMusica onSelecionada={handleSelecionarMusica} />
        </div>
      </div>
    </div>
  );

  // ── Fase: tocando + escolher vencedor ─────────────────
  return (
    <div className="app-wrapper" style={{ paddingTop: 80, paddingBottom: 40 }}>
      <div className="card" style={{ maxWidth: 540 }}>
        <div className="card-header">
          <div className="title-eyebrow">🎵 Adivinhe a Música · Rodada {musicaRodada}/{numRodadas}</div>
          {musicaSelecionada && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, background: "rgba(62,170,255,0.1)", border: "1px solid #3eaaff40", borderRadius: 10, padding: "10px 14px" }}>
              {musicaSelecionada.thumb && <img src={musicaSelecionada.thumb} alt="" style={{ width: 48, height: 36, borderRadius: 6, objectFit: "cover" }} />}
              <div>
                <div style={{ fontWeight: 700, color: "#3eaaff", fontSize: "0.9rem" }}>{musicaSelecionada.titulo}</div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>{musicaSelecionada.canal}</div>
              </div>
            </div>
          )}
        </div>
        <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Player */}
          {musicaSelecionada && <YouTubePlayer videoId={musicaSelecionada.videoId} />}

          <div style={{ background: "rgba(62,170,255,0.08)", border: "1px solid #3eaaff30", borderRadius: 10, padding: "10px 14px", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
            🎧 Toque apenas os <strong style={{ color: "#3eaaff" }}>primeiros segundos</strong> e pause — depois selecione quem acertou!
          </div>

          {/* Quem acertou */}
          <div style={{ fontWeight: 700, color: "var(--text-secondary)", fontSize: "0.88rem" }}>🏆 Quem acertou primeiro?</div>
          {ouvintes.map(({ nome, idx }) => {
            const av = AVATARS[idx % AVATARS.length];
            const selecionado = vencedor === idx;
            return (
              <button key={idx} onClick={() => { setVencedor(idx); setNinguem(false); }} style={{
                display: "flex", alignItems: "center", gap: 14,
                background: selecionado ? av.bg : "var(--bg-elevated)",
                border: `2px solid ${selecionado ? av.border : "var(--border)"}`,
                borderRadius: 12, padding: "12px 16px", cursor: "pointer",
                transition: "all 0.15s", boxShadow: selecionado ? `0 0 20px ${av.border}40` : "none",
              }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: av.bg, border: `2px solid ${av.border}`, color: av.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem" }}>{av.emoji}</div>
                <div style={{ fontWeight: 700, color: selecionado ? av.text : "var(--text-primary)", flex: 1 }}>{nome}</div>
                <div style={{ fontFamily: "'Space Mono'", fontSize: "0.82rem", color: "var(--text-muted)" }}>
                  {musicaPlacar[idx] || 0} pt{(musicaPlacar[idx] || 0) !== 1 ? "s" : ""}
                </div>
                {selecionado && <span style={{ color: av.text, fontSize: "1.2rem" }}>⭐</span>}
              </button>
            );
          })}

          <button onClick={() => { setNinguem(true); setVencedor(null); }} style={{
            background: ninguem ? "rgba(255,62,62,0.15)" : "var(--bg-elevated)",
            border: `2px solid ${ninguem ? "var(--accent)" : "var(--border)"}`,
            borderRadius: 12, padding: "14px 16px", cursor: "pointer",
            color: ninguem ? "var(--accent)" : "var(--text-secondary)",
            fontWeight: 700, fontSize: "0.9rem", transition: "all 0.15s",
          }}>😞 Ninguém acertou</button>

          <button className="btn btn-ghost" onClick={() => setFase("busca")} style={{ fontSize: "0.82rem", color: "var(--text-muted)" }}>
            ← Trocar música
          </button>
        </div>
        <div className="card-footer" style={{ gap: 10 }}>
          {musicaRodada >= numRodadas && (
            <button className="btn btn-secondary" onClick={finalizarMusica}>🏆 Fim do jogo</button>
          )}
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={vencedor === null && !ninguem} onClick={confirmarVencedor}>
            Confirmar →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tela de resultado / placar ─────────────────────────────────
export function MusicaResultadoScreen() {
  const { jogadores, musicaDJIdx, musicaPlacar, musicaRodada, proximaRodadaMusica, finalizarMusica, numRodadas } = useGame();
  const ultimaRodada = musicaRodada >= numRodadas;

  const placarOrdenado = Object.entries(musicaPlacar)
    .map(([idx, pts]) => ({ idx: Number(idx), nome: jogadores[Number(idx)], pts }))
    .sort((a, b) => b.pts - a.pts);

  return (
    <div className="app-wrapper" style={{ paddingTop: 80, paddingBottom: 40 }}>
      <div className="card" style={{ maxWidth: 480 }}>
        <div className="card-header">
          <div className="title-eyebrow">🎵 Adivinhe a Música</div>
          <h2 className="title-main" style={{ fontSize: "2rem" }}>
            {ultimaRodada
              ? <span style={{ color: "#3eaaff" }}>Placar final! 🏆</span>
              : <span>Após rodada <span style={{ color: "#3eaaff" }}>{musicaRodada}/{numRodadas}</span></span>
            }
          </h2>
        </div>
        <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {placarOrdenado.map(({ idx, nome, pts }, pos) => {
            const av = AVATARS[idx % AVATARS.length];
            const isDJ = idx === musicaDJIdx;
            return (
              <div key={idx} style={{
                display: "flex", alignItems: "center", gap: 12,
                background: "var(--bg-elevated)", borderRadius: 12, padding: "14px 16px",
                border: `1px solid ${pos === 0 && pts > 0 ? "var(--gold)" : "var(--border)"}`,
              }}>
                <div style={{ width: 28, fontSize: "1.2rem", textAlign: "center" }}>
                  {pos === 0 && pts > 0 ? "👑" : `${pos + 1}°`}
                </div>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: av.bg, border: `2px solid ${av.border}`, color: av.text, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem" }}>{av.emoji}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{nome}</div>
                  {isDJ && <div style={{ fontSize: "0.72rem", color: "#3eaaff" }}>🎵 DJ desta rodada</div>}
                </div>
                <div style={{ fontFamily: "'Bebas Neue'", fontSize: "1.6rem", color: pos === 0 && pts > 0 ? "var(--gold)" : "#3eaaff" }}>
                  {pts} pt{pts !== 1 ? "s" : ""}
                </div>
              </div>
            );
          })}
        </div>
        <div className="card-footer" style={{ gap: 10 }}>
          <button className="btn btn-secondary" onClick={finalizarMusica}>
            {ultimaRodada ? "🏆 Ver placar final" : "🏠 Encerrar"}
          </button>
          {!ultimaRodada && (
            <button className="btn btn-primary" onClick={proximaRodadaMusica}>🎵 Próxima rodada →</button>
          )}
        </div>
      </div>
    </div>
  );
}
